import classNames from "classnames";
import { isEqual, isNil } from "lodash";
import AppContext from "PFApp/app_context";
import { AVAILABILITY_MODES, AvailabilityInput } from "PFComponents/availability";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useTemplate } from "PFCore/helpers/templates";
import InfoIcon from "PFIcons/info.svg";
import { AvailabilityMode } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { letFilterRestore } from "../../use_filtered_collection/format_filters";
import css from "./availability_filter_item.less";

const AVAILABILITY_SCOPE_LIMIT = 3000;

const AvailabilityFilterItem = ({ filter, handleChange, scope = 0, templateKey = "task", portalRef }) => {
  const {
    store: { currentAccount }
  } = useContext(AppContext);
  const { t } = useTranslation("availability");

  const template = useTemplate(templateKey);
  const hasReachedScopeLimit = scope > AVAILABILITY_SCOPE_LIMIT;

  const defaultAvailabilityThreshold = template?.schema.properties?.availability?.default_threshold;
  const defaultAvailabilityMode = template?.schema.properties?.availability?.default_mode;
  const allowedModes = template.schema.properties?.availability?.allowed_modes || AVAILABILITY_MODES;

  const filterValue = useMemo(() => filter.value || {}, [filter.value]);

  const letRestore = letFilterRestore(filter);

  const [availability, setAvailability] = useState(filterValue);

  useEffect(() => {
    setAvailability(filterValue);
  }, [filterValue]);

  const handleAvailabilityChange = (newValue, timeRuleModeChecked) => {
    if (isEqual(newValue, availability)) {
      return;
    }

    if (availability && !newValue) {
      const mode = !isNil(timeRuleModeChecked)
        ? { mode: timeRuleModeChecked ? AvailabilityMode.TimeRule : AvailabilityMode.Within }
        : {};

      handleChange(newValue);
      setAvailability({ ...mode });
      return;
    }

    const rangesToSave = newValue.ranges?.filter((range) => range.start && range.end) || [];
    const valueToSave = { ...newValue, ranges: rangesToSave };

    if ((rangesToSave.length && !isEqual(valueToSave, availability)) || !newValue.ranges.length) {
      setAvailability(newValue);
      handleChange(valueToSave);
    }
  };

  return (
    <div className={classNames(css.root)}>
      <div
        className={classNames({
          [css.scopeLimitReached]: hasReachedScopeLimit
        })}
      >
        <AvailabilityInput
          availability={availability}
          accountAvailability={currentAccount.availability}
          defaultAvailabilityMode={defaultAvailabilityMode}
          defaultAvailabilityThreshold={defaultAvailabilityThreshold}
          allowedModes={allowedModes}
          onChange={handleAvailabilityChange}
          isFilter
          onRestore={
            letRestore
              ? () => {
                  handleChange(filter.defaultValue);
                }
              : undefined
          }
          portalRef={portalRef}
        />
      </div>
      {hasReachedScopeLimit && (
        <Tooltip content={<div>{t("disabledByLimit.description")}</div>}>
          <div className={css.scopeDisclaimer}>
            {t("disabledByLimit.message")}
            <span>
              <InfoIcon height={24} width={24} />
            </span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

AvailabilityFilterItem.propTypes = {
  handleChange: PropTypes.func.isRequired,
  scope: PropTypes.number,
  filter: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.oneOf(["availability"]),
    value: PropTypes.oneOfType([
      PropTypes.oneOf([""]), // empty string is valis and consider null
      PropTypes.shape({
        mode: PropTypes.string,
        start: PropTypes.string,
        end: PropTypes.string,
        duration: PropTypes.shape({ mode: PropTypes.string, value: PropTypes.string })
      })
    ]),
    defaultValue: PropTypes.oneOfType([
      PropTypes.oneOf([""]), // empty string is valis and consider null
      PropTypes.shape({
        mode: PropTypes.string,
        start: PropTypes.string,
        end: PropTypes.string,
        duration: PropTypes.shape({ mode: PropTypes.string, value: PropTypes.string })
      })
    ])
  }).isRequired,
  templateKey: PropTypes.string,
  portalRef: PropTypes.object
};

export default AvailabilityFilterItem;
